import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { environment } from 'src/environments/environment';
import { CatalogAttributeV2, CatalogResponse, CatalogResponseV2, KpiDashboardField, KpiJourneyField, MergedField } from '../models/catalog.model';
import { AssetMappingConfig, getFieldConfig, extractNameFromFieldConfigKey } from '../models/asset-config.model';
import { MAP_FIELD_SCOPES, JOURNEY_FIELD_SCOPES } from '../config/config.constants';
import { AppStateInterface } from 'src/app/appState.interface';
import { getApplySelectedCustomer } from '../store/selectors';
import { AdditionalSignalsScope, MappingConfig } from '../models/iotAssetsModel.data';
import { CurrentUserInfo } from '../models/currentUserInfo';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root',
})
export class CatalogService implements OnDestroy {
  private readonly apiUrl = environment.baseTsOhwUrl;
  private readonly configEndpoint = 'v1/rt/config/general';
  private destroy$ = new Subject<void>();
  private readonly MAPPING_CACHE_TTL = 30000;
  private mappingConfigCache = new Map<string, { data: MappingConfig[]; timestamp: number }>();
  private journeyConfigCache = new Map<string, { data: MappingConfig[]; timestamp: number }>();
  private dashboardConfigCache = new Map<string, { data: MappingConfig[]; timestamp: number }>();
  private graphicsConfigCache = new Map<string, { data: MappingConfig[]; timestamp: number }>();
  // Deduplicazione richieste in volo: se più componenti chiamano getCatalogForCustomer
  // nello stesso tick, condividono un unico HTTP invece di sparare N chiamate separate.
  // La entry viene rimossa appena la risposta arriva, quindi la chiamata successiva
  // (dopo il salvataggio, ecc.) andrà sempre all'API.
  private pendingRequests = new Map<string, Observable<CatalogResponseV2>>();
  private catalogDataCache = new Map<string, CatalogAttributeV2[]>();
  // Dedup richieste mapping_config in volo: al bootstrap la stessa config viene richiesta da più
  // sorgenti (catalog effect + iotAssets.service). La cache a tempo non basta perché le chiamate
  // partono prima che una popoli la cache: qui condividono un unico HTTP finché è in volo.
  private mappingConfigPending = new Map<string, Observable<MappingConfig[]>>();

  constructor(
    private http: HttpClient,
    private store: Store<AppStateInterface>,
    private dataService: DataService,
    private translateService: TranslateService
  ) {}

  getScopeLabel(scope: string): string {
    const key = `scopeNames.${scope}`;
    const translated = this.translateService.instant(key);
    return translated !== key ? translated : scope.toUpperCase();
  }

  getScopeDescription(scope: string): string {
    const key = `scopeDescriptions.${scope}`;
    const translated = this.translateService.instant(key);
    return translated !== key ? translated : '';
  }

  /**
   * Etichetta di un attributo del catalogo nella lingua corrente:
   * i18nLabel[lang-LANG] -> label -> name (stessa regola di signals-catalog20).
   */
  getAttributeDisplayLabel(attr: { name: string; label?: string | null; i18nLabel?: { [key: string]: string } | null }): string {
    if (attr.i18nLabel) {
      const currentLang = localStorage.getItem('language') || 'en';
      const currentLangKey = `${currentLang}-${currentLang.toUpperCase()}`;
      if (attr.i18nLabel[currentLangKey]) {
        return attr.i18nLabel[currentLangKey];
      }
    }
    return attr.label || attr.name;
  }

  getCatalog(): Observable<CatalogResponse> {
    return this.store.pipe(
      select(getApplySelectedCustomer),
      take(1),
      switchMap((selectedCustomer) => {
        const uidCustomer = selectedCustomer?.uid;

        if (!uidCustomer) {
          console.error('❌ Impossibile recuperare catalogo: customer non selezionato');
          return throwError(() => new Error('Customer non selezionato per il recupero del catalogo'));
        }

        const url = `${this.apiUrl}v2/rt/catalog?uidCustomer=${uidCustomer}`;

        return this.http.get<CatalogResponse>(url).pipe(
          tap(() => console.log('✅ Catalogo ricevuto per customer:', uidCustomer)),
          catchError((error) => {
            console.error('Errore durante la chiamata a getCatalog:', error);
            return throwError(() => new Error('Errore durante il recupero del catalogo.'));
          })
        );
      }),
      takeUntil(this.destroy$)
    );
  }

  getLastCatalogForCustomer(customerId: string): CatalogAttributeV2[] {
    return this.catalogDataCache.get(customerId) ?? [];
  }

  getCatalogForCustomer(customerId: string): Observable<CatalogResponseV2> {
    const pending = this.pendingRequests.get(customerId);
    if (pending) {
      return pending;
    }

    const url = `${this.apiUrl}v2/rt/catalog?uidCustomer=${customerId}`;
    const request$ = this.http.get<any>(url).pipe(
      map((raw): CatalogResponseV2 => {
        const isEnvelope = raw.data && !Array.isArray(raw.data);
        return {
          ...raw,
          data: isEnvelope ? (raw.data.items ?? []) : (raw.data ?? []),
          uidConfiguration: isEnvelope ? (raw.data.uidConfiguration ?? null) : null,
          createdByCustomerUid: isEnvelope ? (raw.data.createdByCustomerUid ?? null) : null,
          createdByCustomerName: isEnvelope ? (raw.data.createdByCustomerName ?? null) : null,
        };
      }),
      tap((res) => {
        this.catalogDataCache.set(customerId, res.data ?? []);
        console.log('✅ [CatalogService] Catalogo caricato per customer:', customerId);
      }),
      catchError(() => throwError(() => new Error('Errore durante il recupero del catalogo.'))),
      finalize(() => this.pendingRequests.delete(customerId)),
      shareReplay(1)
    );

    this.pendingRequests.set(customerId, request$);
    return request$;
  }

  saveCatalogConfiguration(catalogData: any, customerUid: string, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
    if (!catalogData) {
      console.warn('Nessuna configurazione catalog da salvare');
      return of(null);
    }
    return this.saveCatalogConfigToAPI(catalogData, customerUid, audience);
  }

  deleteCatalogConfiguration(configUid: string): Observable<any> {
    if (!configUid) {
      console.warn('UID configurazione mancante, impossibile eliminare');
      return of(null);
    }
    return this.http.delete(`${this.apiUrl}v1/rt/config/?id=${configUid}`).pipe(
      tap(() => console.log('✅ Configurazione catalog eliminata, UID:', configUid)),
      catchError((error) => {
        console.error('Errore eliminazione configurazione catalog:', error);
        return throwError(() => error);
      })
    );
  }

  private saveCatalogConfigToAPI(catalogData: any, customerUid: string, audience: 'USER' | 'CUSTOMER' | 'GLOBAL'): Observable<any> {
    const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
    const uidUser = userInfo.user?.uid;

    if (!customerUid || !uidUser) {
      console.warn('Impossibile salvare catalog: mancano credenziali', { uidCustomer: !!customerUid, uidUser: !!uidUser });
      return of(null);
    }

    const payload: MappingConfig = {
      uidUser,
      scope: AdditionalSignalsScope.CATALOG_CONFIG,
      audience,
      config: {
        name: 'Signals Catalog Configuration',
        description: 'Catalogo generale con metadati',
        mappingData: catalogData,
        state: 'active',
        type: 'catalog',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        customers: [customerUid],
      },
    };

    console.log('📤 [CatalogService] Salvataggio catalog:', JSON.stringify(payload, null, 2));

    return this.http.post(`${this.apiUrl}${this.configEndpoint}`, payload).pipe(
      tap(() => console.log('✅ [CatalogService] Configurazione catalog salvata')),
      catchError((error) => {
        console.error('[CatalogService] Errore salvataggio catalog:', error);
        return throwError(() => error);
      })
    );
  }

  saveMappingConfiguration(mappingConfig: AssetMappingConfig | null, customerUid: string, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
    if (!mappingConfig) {
      console.warn('Nessuna configurazione mapping da salvare');
      return of(null);
    }
    console.log('💾 Salvataggio configurazione mapping corrente con audience:', audience);
    return this.saveMappingConfigToAPI(mappingConfig, customerUid, audience);
  }

  private saveMappingConfigToAPI(mappingConfig: AssetMappingConfig, customerUid: string, audience: 'USER' | 'CUSTOMER' | 'GLOBAL'): Observable<any> {
    const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
    const uidUser = userInfo.user?.uid;

    if (!customerUid || !uidUser) {
      console.warn('Impossibile salvare mapping: mancano credenziali', { uidCustomer: !!customerUid, uidUser: !!uidUser });
      return of(null);
    }

    mappingConfig.audience = audience;

    const payload: MappingConfig = {
      uidUser,
      scope: AdditionalSignalsScope.MAPPING_CONFIG,
      audience,
      config: {
        name: 'Vehicle Mapping Configuration',
        description: 'Configurazione di mapping per i campi dei veicoli IoT',
        mappingData: mappingConfig,
        state: 'active',
        type: 'mapping',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        customers: [customerUid],
      },
    };

    console.log('📤 [CatalogService] Salvataggio mapping config:', JSON.stringify(payload, null, 2));

    return this.http.post(`${this.apiUrl}${this.configEndpoint}`, payload).pipe(
      tap(() => console.log('✅ [CatalogService] Configurazione mapping salvata')),
      catchError((error) => {
        console.error('[CatalogService] Errore salvataggio mapping:', error);
        return throwError(() => error);
      })
    );
  }

  getMappingConfigFromAPI(uidCustomer: string, uidUser: string): Observable<MappingConfig[]> {
    const cacheKey = uidCustomer;
    const now = Date.now();
    const audience = 'CUSTOMER';
    const cached = this.mappingConfigCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < this.MAPPING_CACHE_TTL) {
      console.log('✅ getMappingConfigFromAPI: cache hit per customer:', uidCustomer);
      return of(cached.data);
    }

    // Se una richiesta per lo stesso customer è già in volo, la condividiamo (evita il doppione al bootstrap)
    const pending = this.mappingConfigPending.get(cacheKey);
    if (pending) {
      return pending;
    }

    const queryString = `?scope=${AdditionalSignalsScope.MAPPING_CONFIG}&uidUser=${uidUser}&uidCustomer=${uidCustomer}&audience=${audience}`;
    const request$ = this.http.get(`${this.apiUrl}${this.configEndpoint}${queryString}`).pipe(
      map((res: any) => {
        const data: MappingConfig[] = res.data || [];
        this.mappingConfigCache.set(cacheKey, { data, timestamp: now });
        return data;
      }),
      catchError((error) => {
        console.error('Errore getMappingConfigFromAPI:', error);
        return of([]);
      }),
      finalize(() => this.mappingConfigPending.delete(cacheKey)),
      shareReplay(1)
    );
    this.mappingConfigPending.set(cacheKey, request$);
    return request$;
  }

  invalidateMappingCache(uidCustomer: string): void {
    this.mappingConfigCache.delete(uidCustomer);
  }

  getJourneyConfigFromAPI(uidCustomer: string, uidUser: string): Observable<MappingConfig[]> {
    const cacheKey = uidCustomer;
    const now = Date.now();
        const audience = 'CUSTOMER';
    const cached = this.journeyConfigCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < this.MAPPING_CACHE_TTL) {
      return of(cached.data);
    }

    const queryString = `?scope=${AdditionalSignalsScope.JOURNEY_CONFIG}&uidUser=${uidUser}&uidCustomer=${uidCustomer}&audience=${audience}`;
    return this.http.get(`${this.apiUrl}${this.configEndpoint}${queryString}`).pipe(
      map((res: any) => {
        const data: MappingConfig[] = res.data || [];
        this.journeyConfigCache.set(cacheKey, { data, timestamp: now });
        return data;
      }),
      catchError((error) => {
        console.error('Errore getJourneyConfigFromAPI:', error);
        return of([]);
      })
    );
  }

  saveJourneyConfiguration(journeyData: any, customerUid: string, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<MappingConfig[]> {
    const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
    const uidUser = userInfo.user?.uid;

    if (!customerUid || !uidUser) {
      console.warn('Impossibile salvare journey: mancano credenziali');
      return of([] as MappingConfig[]);
    }

    const payload: MappingConfig = {
      uidUser,
      scope: AdditionalSignalsScope.JOURNEY_CONFIG,
      audience,
      config: {
        name: 'KPI Journey Configuration',
        description: 'Configurazione dei KPI visualizzati nel journey',
        mappingData: journeyData,
        state: 'active',
        type: 'journey',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        customers: [customerUid],
      },
    };

    return this.http.post(`${this.apiUrl}${this.configEndpoint}`, payload).pipe(
      map((res: any) => {
        const raw = res?.data ?? res;
        const saved: MappingConfig[] = Array.isArray(raw) ? raw : (raw ? [raw] : [payload]);
        this.journeyConfigCache.set(customerUid, { data: saved, timestamp: Date.now() });
        return saved;
      }),
      catchError((error) => {
        console.error('[CatalogService] Errore salvataggio journey:', error);
        return throwError(() => error);
      })
    );
  }

  invalidateJourneyCache(uidCustomer: string): void {
    this.journeyConfigCache.delete(uidCustomer);
  }

  getDashboardConfigFromAPI(uidCustomer: string, uidUser: string): Observable<MappingConfig[]> {
    const cacheKey = uidCustomer;
    const now = Date.now();
        const audience = 'CUSTOMER';
    const cached = this.dashboardConfigCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < this.MAPPING_CACHE_TTL) {
      return of(cached.data);
    }
    const queryString = `?scope=${AdditionalSignalsScope.DASHBOARD_CONFIG}&uidUser=${uidUser}&uidCustomer=${uidCustomer}&audience=${audience}`;
    return this.http.get(`${this.apiUrl}${this.configEndpoint}${queryString}`).pipe(
      map((res: any) => {
        const data: MappingConfig[] = res.data || [];
        this.dashboardConfigCache.set(cacheKey, { data, timestamp: now });
        return data;
      }),
      catchError((error) => {
        console.error('Errore getDashboardConfigFromAPI:', error);
        return of([]);
      })
    );
  }

  saveDashboardConfiguration(dashboardData: any, customerUid: string, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<MappingConfig[]> {
    const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
    const uidUser = userInfo.user?.uid;

    if (!customerUid || !uidUser) {
      console.warn('Impossibile salvare dashboard: mancano credenziali');
      return of([] as MappingConfig[]);
    }

    const payload: MappingConfig = {
      uidUser,
      scope: AdditionalSignalsScope.DASHBOARD_CONFIG,
      audience,
      config: {
        name: 'Dashboard Configuration',
        description: 'Configurazione dei KPI visualizzati nel dashboard',
        mappingData: dashboardData,
        state: 'active',
        type: 'dashboard',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        customers: [customerUid],
      },
    };

    return this.http.post(`${this.apiUrl}${this.configEndpoint}`, payload).pipe(
      map((res: any) => {
        const raw = res?.data ?? res;
        const saved: MappingConfig[] = Array.isArray(raw) ? raw : (raw ? [raw] : [payload]);
        this.dashboardConfigCache.set(customerUid, { data: saved, timestamp: Date.now() });
        return saved;
      }),
      catchError((error) => {
        console.error('[CatalogService] Errore salvataggio dashboard:', error);
        return throwError(() => error);
      })
    );
  }

  invalidateDashboardCache(uidCustomer: string): void {
    this.dashboardConfigCache.delete(uidCustomer);
  }

  getGraphicsConfigFromAPI(uidCustomer: string, uidUser: string): Observable<MappingConfig[]> {
    const cacheKey = uidCustomer;
    const now = Date.now();
    const audience = 'CUSTOMER';
    const cached = this.graphicsConfigCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < this.MAPPING_CACHE_TTL) {
      return of(cached.data);
    }

    const queryString = `?scope=${AdditionalSignalsScope.GRAPHICS_CONFIG}&uidUser=${uidUser}&uidCustomer=${uidCustomer}&audience=${audience}`;
    return this.http.get(`${this.apiUrl}${this.configEndpoint}${queryString}`).pipe(
      map((res: any) => {
        const data: MappingConfig[] = res.data || [];
        this.graphicsConfigCache.set(cacheKey, { data, timestamp: now });
        return data;
      }),
      catchError((error) => {
        console.error('[CatalogService] Errore getGraphicsConfigFromAPI:', error);
        return throwError(() => new Error('Errore durante il recupero della configurazione graphics.'));
      })
    );
  }

  invalidateGraphicsCache(uidCustomer: string): void {
    this.graphicsConfigCache.delete(uidCustomer);
  }

  buildDashboardFields(
    catalogItems: CatalogAttributeV2[],
    dashboardConfigs: MappingConfig[],
    lang: string = localStorage.getItem('language') || 'en'
  ): KpiDashboardField[] {
    const langKey = `${lang}-${lang.toUpperCase()}`;
    const savedDashboard: any[] = dashboardConfigs?.[0]?.config?.mappingData?.dashboardConfig ?? [];

    const savedByUid = new Map<string, any>();
    const savedByNameScope = new Map<string, any>();
    savedDashboard.forEach(saved => {
      if (saved.uid) savedByUid.set(saved.uid, saved);
      if (saved.name && saved.scope) savedByNameScope.set(`${saved.name}|${saved.scope}`, saved);
    });

    const allScopedNames = new Set(catalogItems.map(i => `${i.name}|${i.scope}`));

    // Primo passaggio: item dal catalogo
    const result: KpiDashboardField[] = catalogItems
      .map((item, index) => {
        // Con uid l'abbinamento è per uid; per nome+scope solo con voci salvate senza uid (dati legacy):
        // due KPI custom diversi possono avere lo stesso nome e scope (es. due KPI sul segnale "ad")
        const byNameScope = savedByNameScope.get(`${item.name}|${item.scope}`);
        const saved = item.uid
          ? (savedByUid.get(item.uid) ?? (byNameScope && !byNameScope.uid ? byNameScope : undefined))
          : byNameScope;
        return {
          uid: item.uid ?? undefined,
          name: item.name,
          label: item.i18nLabel?.[langKey] || item.label || item.name,
          scope: item.scope,
          type: item.type ?? undefined,
          unit: item.unit ?? undefined,
          kpi: item.kpi,
          i18nLabel: item.i18nLabel ?? undefined,
          showInDashboard: !!saved,
          order: saved ? (saved.order ?? 0) : savedDashboard.length + index,
          deletedFromCatalog: false,
          disabledInCatalog: item.visible === false
        };
      });

    // Secondo passaggio: item salvati nel dashboard ma non più in catalogo → deletedFromCatalog
    savedDashboard.forEach((saved, index) => {
      const alreadyIncluded = result.some(r =>
        (saved.uid && r.uid === saved.uid) || `${r.name}|${r.scope}` === `${saved.name}|${saved.scope}`
      );
      if (alreadyIncluded) return;
      if (!allScopedNames.has(`${saved.name}|${saved.scope}`)) {
        result.push({
          uid: saved.uid ?? undefined,
          name: saved.name,
          label: saved.label || saved.name,
          scope: saved.scope ?? 'other',
          type: saved.type ?? undefined,
          unit: saved.unit ?? undefined,
          kpi: saved.kpi ?? false,
          i18nLabel: saved.i18nLabel ?? undefined,
          showInDashboard: true,
          order: saved.order ?? savedDashboard.length + result.length + index,
          deletedFromCatalog: true,
          disabledInCatalog: false
        });
      }
    });

    result.sort((a, b) => {
      const oa = a.showInDashboard ? a.order : 999999;
      const ob = b.showInDashboard ? b.order : 999999;
      return oa - ob;
    });

    return result;
  }

  invalidateCache(_customerId: string): void {}

  /**
   * Merge catalogo + mapping_config per la configurazione campi mappa.
   * - Filtra per MAP_FIELD_SCOPES + kpi/kpi_custom
   * - isNew = campo nel catalogo ma non ancora in mapping_config
   * - disabledInCatalog = campo nel catalogo con visible=false: viene mostrato nella sezione
   *   "disabilitati" (come in Attività/Dashboard), anche se non è ancora in mapping_config
   * - deletedFromCatalog = campo in mapping_config ma non più nel catalogo (confronto per scope+name)
   */
  buildMapConfigFields(
    catalogItems: CatalogAttributeV2[],
    mappingConfig: AssetMappingConfig | null,
    lang: string = localStorage.getItem('language') || 'en'
  ): { [scope: string]: MergedField[] } {
    if (!catalogItems?.length || !mappingConfig) return {};

    const langKey = `${lang}-${lang.toUpperCase()}`;
    const result: { [scope: string]: MergedField[] } = {};

    // Oltre agli scope "mappa" includiamo anche i KPI personalizzati, altrimenti
    // la loro label i18n non verrebbe mai risolta in base alla lingua corrente
    // (vedi mergedFieldsMap in side-bar-map/actia-vehicle-details/actia-map popup).
    const includedScopes: string[] = [...MAP_FIELD_SCOPES, 'kpi', 'kpi_custom'];
    const scopedKey = (scope: string, name: string) => `${scope}|${name}`;

    // Primo passaggio: tutti gli item del catalogo negli scope inclusi, anche quelli con visible=false
    const catalogKeys = new Set<string>();
    catalogItems
      .filter(item => includedScopes.includes(item.scope as any))
      .forEach(item => {
        const scope = item.scope || 'other';
        if (!result[scope]) result[scope] = [];
        catalogKeys.add(scopedKey(scope, item.name));

        const mappingEntry = getFieldConfig(mappingConfig, scope, item.name);
        const label = item.i18nLabel?.[langKey] || item.label || item.name;
        const disabledInCatalog = item.visible === false;

        result[scope].push({
          key: item.name,
          label,
          scope,
          unit: item.unit,
          kpi: item.kpi,
          isNew: !mappingEntry && !disabledInCatalog,
          deletedFromCatalog: false,
          disabledInCatalog,
          visible: mappingEntry?.visible !== false,
          availableInModels: mappingEntry?.availableInModels ?? item.canMaps
        });
      });

    // Secondo passaggio: campi in mapping_config negli scope inclusi non più presenti nel catalogo
    // (stesso scope+name) → deletedFromCatalog
    if (mappingConfig.fields) {
      Object.entries(mappingConfig.fields).forEach(([key, config]) => {
        const scope = config.scope || 'other';
        const name = extractNameFromFieldConfigKey(key);
        if (!includedScopes.includes(scope) || catalogKeys.has(scopedKey(scope, name))) return;
        if (!result[scope]) result[scope] = [];

        result[scope].push({
          key: name,
          label: config.label || name,
          scope,
          unit: config.unit ?? null,
          kpi: false,
          isNew: false,
          deletedFromCatalog: true,
          disabledInCatalog: false,
          visible: config.visible !== false,
          availableInModels: config.availableInModels
        });
      });
    }

    return result;
  }

  buildJourneyFields(
    catalogItems: CatalogAttributeV2[],
    journeyConfigs: MappingConfig[],
    lang: string = localStorage.getItem('language') || 'en'
  ): KpiJourneyField[] {
    const langKey = `${lang}-${lang.toUpperCase()}`;
    const savedJourney: any[] = journeyConfigs?.[0]?.config?.mappingData?.kpiJourney ?? [];

    // Abbinamento per uid oppure per nome+scope: lo stesso nome può esistere in più scope
    // (es. "ad" in kpi e kpi_custom) e non devono condividere la selezione.
    const scopedKey = (name: string, scope: string | undefined) => `${name}|${scope ?? ''}`;
    const savedByUid = new Map<string, any>();
    const savedByNameScope = new Map<string, any>();
    savedJourney.forEach(saved => {
      if (saved.uid) savedByUid.set(saved.uid, saved);
      if (saved.name) savedByNameScope.set(scopedKey(saved.name, saved.scope), saved);
    });

    // Tutti gli item nel catalogo con scope journey (inclusi visible=false)
    const allScopedItems = catalogItems.filter(
      item => (JOURNEY_FIELD_SCOPES as readonly string[]).includes(item.scope)
    );
    const allScopedNames = new Set(allScopedItems.map(i => scopedKey(i.name, i.scope)));

    // Un KPI (scope 'kpi'/'kpi_custom') può basarsi su un segnale con lo stesso name ma scope diverso
    // (es. 'signal'). Se quel segnale è visible=false, il KPI va trattato come disabilitato
    // anche se la sua entry ha visible=true (stessa logica di isKpiDisabled in kpi-configuration).
    const isDisabledViaUnderlyingItem = (item: CatalogAttributeV2): boolean =>
      catalogItems.some(other => other.name === item.name && other.scope !== item.scope && other.visible === false);

    // Primo passaggio: item dal catalogo
    const result: KpiJourneyField[] = allScopedItems.map((item, index) => {
      // Con uid l'abbinamento è per uid; per nome+scope solo con voci salvate senza uid (dati legacy):
      // due KPI custom diversi possono avere lo stesso nome e scope (es. due KPI sul segnale "ad")
      const byNameScope = savedByNameScope.get(scopedKey(item.name, item.scope));
      const saved = item.uid
        ? (savedByUid.get(item.uid) ?? (byNameScope && !byNameScope.uid ? byNameScope : undefined))
        : byNameScope;
      return {
        uid: item.uid ?? undefined,
        name: item.name,
        label: item.i18nLabel?.[langKey] || item.label || item.name,
        scope: item.scope,
        type: item.type ?? undefined,
        unit: item.unit ?? undefined,
        kpi: item.kpi,
        i18nLabel: item.i18nLabel ?? undefined,
        showInJourney: !!saved,
        order: saved ? (saved.order ?? 0) : savedJourney.length + index,
        deletedFromCatalog: false,
        disabledInCatalog: item.visible === false || isDisabledViaUnderlyingItem(item)
      };
    });

    // Secondo passaggio: item salvati nel journey ma non più in catalogo → deletedFromCatalog
    savedJourney.forEach((saved, index) => {
      const alreadyIncluded = result.some(r =>
        (saved.uid && r.uid === saved.uid) || scopedKey(r.name, r.scope) === scopedKey(saved.name, saved.scope)
      );
      if (alreadyIncluded) return;
      if (!allScopedNames.has(scopedKey(saved.name, saved.scope))) {
        result.push({
          uid: saved.uid ?? undefined,
          name: saved.name,
          label: saved.label || saved.name,
          scope: saved.scope ?? 'other',
          type: saved.type ?? undefined,
          unit: saved.unit ?? undefined,
          kpi: saved.kpi ?? false,
          i18nLabel: saved.i18nLabel ?? undefined,
          showInJourney: true,
          order: saved.order ?? savedJourney.length + result.length + index,
          deletedFromCatalog: true,
          disabledInCatalog: false
        });
      }
    });

    result.sort((a, b) => {
      const oa = a.showInJourney ? a.order : 999999;
      const ob = b.showInJourney ? b.order : 999999;
      return oa - ob;
    });

    return result;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
